import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { APIUrls } from '../enums/apiUrls';
import { servicePlanIdToNewNameMap } from '../enums/enums';
import { ClientsListRequest } from '../interfaces/Admin/Client/ClientsListRequest';
import { ClientsListResponse } from '../interfaces/Admin/Client/ClientsListResponse';
import { GetClientStatusResponse } from '../interfaces/Admin/Client/GetClientStatusResponse';
import { GetHourlyRateResponse } from '../interfaces/Admin/Client/GetHourlyRateResponse';
import { GetServicePlanResponse, ServicePlanList } from '../interfaces/Admin/Client/GetServicePlanListResponse';
import { ClientDetailsResponse } from '../interfaces/Admin/Client/ViewClientDetails';
import { ApiResponse } from '../interfaces/api-response.model';
import {
  ClientPortalUser,
  ClientPortalUserRegistrationResponse,
  RegisterClientPortalUser,
} from '../interfaces/Client/client-portal-api.models';
import { ClientOnStopStatus } from '../interfaces/Client/ClientOnStopStatus';
import { ApiService } from './api.service';
import { getSpecialRequirements, isClientOnStop } from './utility-functions';

@Injectable({
  providedIn: 'root',
})
export class ClientsService {
  constructor(private _apiService: ApiService) {}

  private _servicePlans: GetServicePlanResponse;

  getClientList(clientListReq: ClientsListRequest): Observable<ClientsListResponse> {
    return this._apiService.post<ClientsListResponse>(APIUrls.GetClientList, { ...clientListReq });
  }

  getServicePlanList(): Observable<GetServicePlanResponse> {
    if (this._servicePlans) {
      return of(this._servicePlans);
    }
    return this._apiService.get<GetServicePlanResponse>(APIUrls.GetServicePlanList).pipe(
      switchMap((res: GetServicePlanResponse) => {
        if (res.status) {
          const oldServicePlans = new Set([8, 9, 10, 11, 12, 13]);
          res.result = res.result.map((sp) => {
            return {
              ...sp,
              active: !oldServicePlans.has(sp.id),
              newServicePlanName: servicePlanIdToNewNameMap[sp.id] || sp.servicePlanName,
            } as ServicePlanList;
          });
          this._servicePlans = res;
        }
        return of(res);
      }),
    );
  }

  getHourlyRateList(): Observable<GetHourlyRateResponse> {
    return this._apiService.get<GetHourlyRateResponse>(APIUrls.GetHourlyRate);
  }

  getStatusList(): Observable<GetClientStatusResponse> {
    return this._apiService.get<GetClientStatusResponse>(APIUrls.GetClientStatus);
  }

  getClientDetailsByClientID(clientID: number): Observable<ClientDetailsResponse> {
    return this._apiService.get<ClientDetailsResponse>(`${APIUrls.GetClientDetails}/${clientID}`);
  }

  getClientSpecialRequirements(clientId: number): Observable<ApiResponse<string>> {
    return this._apiService.get<ApiResponse<string>>(`${APIUrls.GetClientSpecialRequirements}?clientId=${clientId}`);
  }

  getClientOnStopStatus(clientId: number): Observable<ClientOnStopStatus> {
    if (!clientId) {
      return of({ isOnStop: false, specialRequirementsText: null });
    }

    return this.getClientSpecialRequirements(clientId).pipe(
      map((response) => {
        const specialRequirements = response?.result;
        const isOnStop = isClientOnStop(specialRequirements);

        return {
          isOnStop,
          specialRequirementsText: isOnStop ? getSpecialRequirements(specialRequirements).join(', ') : null,
        };
      }),
    );
  }

  getClientPortalUsers(clientId: number): Observable<ApiResponse<ClientPortalUser[]>> {
    return this._apiService.get<ApiResponse<ClientPortalUser[]>>(`Client/${clientId}/clientportalusers`).pipe(
      map((response: ApiResponse<ClientPortalUser[]>) => {
        response.result.forEach((user) => {
          user.lastActivityDateUtc = user.lastActivityDateUtc ? new Date(user.lastActivityDateUtc) : undefined;
        });
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        return of({
          status: false,
          statusCode: error.status,
          message: 'Error fetching client portal users',
          result: [],
        });
      }),
    );
  }

  inviteClientPortalUser(
    clientId: number,
    userRegistration: RegisterClientPortalUser,
  ): Observable<ApiResponse<ClientPortalUserRegistrationResponse>> {
    return this._apiService
      .post<ApiResponse<ClientPortalUserRegistrationResponse>>(`Client/${clientId}/clientportalusers`, userRegistration)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return of({
            status: false,
            statusCode: error.status,
            message: 'Error registering client portal user',
            result: {} as ClientPortalUserRegistrationResponse,
          });
        }),
      );
  }
}
